// CrewSidebar.js — docked crew detail panel.
import React from 'react';
import { T } from './lib/crewTokens';
import { hhmm, fmtMi, fmtMin, simNow } from './lib/crewFormat';
import {
  CrewGlyph, OTBadge, TicketProgress, ScheduleChip, Avatar, ClockBadge,
  LiveDuration, DayRibbon, BudgetLegend, EventTimeline, FuelGauge, TruckIcon, statusAccent,
} from './atoms/CrewAtoms';

const CS = T;

function SBSection({ title, sub, children, accent }) {
  return (
    <div style={{ background: CS.surface, border: `1px solid ${CS.line}`, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ padding: '10px 12px 0', display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: accent || CS.ink }}>{title}</span>
        {sub && <span style={{ fontSize: 10.5, fontWeight: 600, color: CS.muteSoft }}>{sub}</span>}
      </div>
      <div style={{ padding: '8px 12px 12px' }}>{children}</div>
    </div>
  );
}

function CrewSidebar({ c, onClose, glyph }) {
  if (!c) return null;
  const v = c.vehicle;
  const accent = statusAccent(c.status.level);
  return (
    <div className="crew-cs-dock" style={{ width: 384, flexShrink: 0, borderLeft: `1px solid ${CS.line}`, background: CS.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      {/* header */}
      <div style={{ background: CS.surface, borderBottom: `1px solid ${CS.line}`, padding: '11px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: CS.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', color: CS.muteSoft, textTransform: 'uppercase' }}>Crews › <span style={{ color: CS.ink2 }}>{c.division}</span></span>
          <button onClick={onClose} title="Close" style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 7, border: `1px solid ${CS.line2}`, background: CS.surface, color: CS.mute, cursor: 'pointer', fontSize: 13 }}>×</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
          <CrewGlyph level={c.status.level} variant={glyph} size={21} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, lineHeight: 1.15 }}>{c.name}</div>
            <div style={{ fontSize: 11, color: CS.mute }}>{c.foreman} · foreman</div>
          </div>
          <OTBadge ot={c.ot} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, background: CS.surface2, border: `1px solid ${CS.lineSoft}`, borderRadius: 8, padding: '7px 9px' }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: c.activity === 'driving' ? CS.orange : CS.green, boxShadow: `0 0 0 3px ${c.activity === 'driving' ? CS.orangeSoft : CS.greenSoft}` }}></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700 }}>{c.location.label}</div>
            <div style={{ fontSize: 9.5, color: CS.muteSoft }}>{c.location.addr}</div>
          </div>
          <span style={{ fontSize: 9.5, color: CS.muteSoft }}>updated <LiveDuration since={simNow() - 47000} /> ago</span>
        </div>
      </div>

      {/* body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {/* why flagged */}
        {c.status.reasons.length > 0 && (
          <div style={{ background: c.status.level === 'critical' ? CS.rustSoft : CS.warnSoft, border: `1px solid ${c.status.level === 'critical' ? '#ecc8b8' : '#ecd3ae'}`, borderRadius: 9, padding: '9px 12px', flexShrink: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: accent, marginBottom: 3 }}>Needs attention</div>
            {c.status.reasons.map((r, i) => <div key={i} style={{ fontSize: 11.5, color: CS.ink2, lineHeight: 1.45, fontWeight: 600 }}>· {r}</div>)}
          </div>
        )}

        {/* day progress */}
        <SBSection title="Today" sub={c.clockIn != null ? `clocked in ${hhmm(c.clockIn)}` : 'no clock data'}>
          <TicketProgress done={c.ticketsDone} scheduled={c.ticketsScheduled} height={8} />
          <div style={{ display: 'flex', gap: 7, marginTop: 9 }}>
            <SBStat label="Service left" value={`${Math.round(c.remServiceMin / 60 * 10) / 10}h`} sub={`${c.remaining} stops`} />
            <SBStat label="Drive left" value={`${c.remDriveMin}m`} sub={`${fmtMi(c.remDriveMin / 2.1)} mi`} />
            <SBStat label="Idle" value={`${c.idleMin}m`} sub="this PM" tone={c.idleMin >= 35 ? 'warn' : 'n'} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, paddingTop: 9, borderTop: `1px solid ${CS.lineSoft}` }}>
            <ScheduleChip behindMin={c.behindMin} />
            <span style={{ fontSize: 10.5, color: CS.mute }}>Last ticket: <b style={{ color: CS.ink2 }}>{c.lastTicket.label}</b> · {c.lastTicket.at ? hhmm(c.lastTicket.at) : '—'}</span>
          </div>
        </SBSection>

        {/* OT projection */}
        <SBSection title="Overtime projection" accent={c.ot.level === 'will' ? CS.rust : c.ot.level === 'approach' ? CS.warn : CS.green}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 23, fontWeight: 800, fontFamily: CS.mono, color: c.ot.level === 'will' ? CS.rust : CS.ink }}>{c.ot.projHrs != null ? `${c.ot.projHrs}h` : '—'}</span>
            <span style={{ fontSize: 11, color: CS.mute }}>projected end of day</span>
            {c.ot.projMin > 0 && <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: CS.rust }}>+{c.ot.projMin}m OT</span>}
          </div>
          {/* 8h budget bar */}
          <div style={{ position: 'relative', height: 9, borderRadius: 99, background: CS.surface3, marginTop: 8 }}>
            <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${Math.min(100, (c.ot.projHrs / 10) * 100)}%`, borderRadius: 99, background: c.ot.level === 'will' ? CS.rust : c.ot.level === 'approach' ? CS.warn : CS.green, opacity: 0.85 }}></div>
            <div title="8h" style={{ position: 'absolute', top: -3, bottom: -3, left: '80%', width: 2, background: CS.ink, borderRadius: 2 }}></div>
          </div>
          <div style={{ fontSize: 9, color: CS.muteSoft, fontWeight: 600, marginTop: 3, letterSpacing: '0.03em' }}>TICK = 8H · BAR = PROJECTED INCL. REMAINING SERVICE + DRIVE</div>
        </SBSection>

        {/* crew members */}
        <SBSection title="Crew" sub={`${c.members.length} assigned`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {c.members.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Avatar name={m.name} initials={m.initials} color={m.color} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{m.name}</div>
                  <div style={{ fontSize: 9.5, color: CS.muteSoft }}>{m.role} · {m.phone}</div>
                </div>
                <ClockBadge status={m.status} since={m.since} dense />
              </div>
            ))}
          </div>
        </SBSection>

        {/* time on the clock */}
        <SBSection title="Time on the clock" sub={`${fmtMin(c.loc.budgetTotal)} tracked today`}>
          <DayRibbon loc={c.loc} height={13} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: CS.muteSoft, fontFamily: CS.mono, marginTop: 3 }}>
            <span>{c.clockIn != null ? hhmm(c.clockIn) : '—'}</span><span style={{ fontStyle: 'italic' }}>in the order it happened</span><span>now</span>
          </div>
          <div style={{ marginTop: 8 }}><BudgetLegend budget={c.loc.budget} /></div>
          <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
            <SBStat label="On-site" value={fmtMin(c.loc.budget.onsite)} sub={`${Math.round(c.loc.budget.onsite / (c.loc.budgetTotal || 1) * 100)}% of day`} tone={c.loc.budget.onsite / (c.loc.budgetTotal || 1) > 0.5 ? 'n' : 'warn'} />
            <SBStat label="Idle elsewhere" value={fmtMin(c.loc.budget.idle)} sub={`+ ${fmtMin(c.loc.budget.tracked)} tracked`} tone={c.loc.budget.idle >= 20 ? 'warn' : 'n'} />
            <SBStat label="Driving" value={fmtMin(c.loc.budget.driving)} sub={`${fmtMin(c.loc.budget.yard)} yard`} />
          </div>
        </SBSection>

        {/* location & GPS */}
        <SBSection title="Location log &amp; GPS" sub={`${c.loc.geofenceFt}ft geofence`} accent={c.loc.flags ? CS.rust : CS.green}>
          {c.loc.flags === 0
            ? <div style={{ fontSize: 11, color: CS.green, fontWeight: 600, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: 99, background: CS.green }}></span>Every punch inside the geofence · phone with the truck</div>
            : <div style={{ fontSize: 11, color: CS.rust, fontWeight: 700, marginBottom: 9 }}>{c.loc.flags} flag{c.loc.flags > 1 ? 's' : ''} today — review the GPS log below</div>}
          <EventTimeline loc={c.loc} />
        </SBSection>

        {/* vehicle */}
        <SBSection title="Vehicle" sub={`${v.no} · ${v.type}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
            <span style={{ width: 34, height: 34, borderRadius: 8, background: CS.surface3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><TruckIcon size={18} color={CS.ink2} /></span>
            <div style={{ flex: 1 }}>
              <FuelGauge pct={v.fuel} width={120} />
              <div style={{ fontSize: 9.5, color: CS.muteSoft, marginTop: 3, fontFamily: CS.mono }}>{v.odometer != null ? v.odometer.toLocaleString() + ' mi' : '—'} · reg {v.regDays < 0 ? `expired ${-v.regDays}d ago` : `due in ${v.regDays}d`}</div>
            </div>
          </div>
          {v.faultCodes.length > 0 && (
            <div style={{ marginBottom: 7 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: CS.rust, marginBottom: 3 }}>Active fault codes</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{v.faultCodes.map((f, i) => <span key={i} style={{ background: CS.rustSoft, color: CS.rust, fontSize: 10, fontWeight: 700, fontFamily: CS.mono, padding: '2px 7px', borderRadius: 5 }}>{f}</span>)}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: CS.muteSoft, marginBottom: 3 }}>Maintenance & repairs</div>
            {v.mtcOverdueMi ? <div style={{ fontSize: 11, color: CS.warn, fontWeight: 700 }}>○ Oil service {v.mtcOverdueMi.toLocaleString()} mi overdue</div> : <div style={{ fontSize: 11, color: CS.green, fontWeight: 600 }}>● Service current</div>}
            {v.openRepairs.map((r, i) => <div key={i} style={{ fontSize: 11, color: r.priority === 'high' ? CS.rust : CS.ink2, fontWeight: r.priority === 'high' ? 700 : 500, marginTop: 2 }}>{r.priority === 'high' ? '● ' : '○ '}{r.item} <span style={{ color: CS.muteSoft, fontSize: 9.5 }}>· open {r.age}d</span></div>)}
            {!v.openRepairs.length && !v.mtcOverdueMi && <div style={{ fontSize: 11, color: CS.muteSoft }}>No open requests</div>}
          </div>
        </SBSection>

        <div style={{ textAlign: 'center', padding: '2px 0 6px', flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: CS.green, cursor: 'pointer' }}>Open full crew view →</span>
          <div style={{ fontSize: 9.5, color: CS.muteSoft, marginTop: 2 }}>GPS history · timecards · job tickets · messages</div>
        </div>
      </div>
    </div>
  );
}

function SBStat({ label, value, sub, tone }) {
  const c = tone === 'warn' ? CS.warn : CS.ink;
  return (
    <div style={{ flex: 1, background: CS.surface2, border: `1px solid ${CS.lineSoft}`, borderRadius: 8, padding: '7px 9px' }}>
      <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: CS.muteSoft, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: c, lineHeight: 1, fontFamily: CS.mono }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: CS.muteSoft, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export { CrewSidebar };
